import React from "react";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import {
  useEditTask,
  useRetrieveCurrentPlanzone,
  useRetrieveElementConstraints,
  useRetrieveSecondaryCriteriaTemplate,
  useTaskRetrieval,
} from "@planzone/view-models";
import { UseCases } from "@planzone/core";
import { useModal } from "@root/client/modal";
import { Loader } from "@common/loader";
import { ProjectRedirection } from "@common/projectRedirection";
import { DatesPickers } from "@scenes/task/datesPickers";
import { Comments } from "@scenes/comments";
import { Safeguard } from "@scenes/safeguard";
import { WorkspaceContext } from "@scenes/workspaceContext";
import { Modal } from "@common/modal";
import { ElementAttachments } from "@common/elementAttachments";
import { ScenePlaceholder } from "@common/scenePlaceholder";
import { ConstraintsPopup } from "@common/constraintsPopup";
import { CogWheelIcon } from "@common/cogWheelIcon";
import { ProjectElementsSecondaryCriteria } from "@scenes/projectElementsSecondaryCriteria";
import { SecondaryCriteriaForm } from "@common/secondaryCriteriaForm";
import { Breadcrumbs } from "@common/breadcrumbs";
import { FolderIcon } from "@common/folderIcon";
import { Props } from "./types";
import { Details } from "./details";
import { Allocations } from "./allocations";
import { Timestamp } from "./timestamp";
import styles from "./styles.scss";

function Task({ isMultiProject, taskId, close }: Props) {
  const { t } = useTranslation();
  const { planzone } = useRetrieveCurrentPlanzone();
  const {
    isRequestPending,
    isRequestFailure,
    task,
    hasRetrievedTask,
    retrieveTask,
  } = useTaskRetrieval(taskId);
  const {
    isRequestPending: isConstraintsRequestPending,
    isRequestSuccess: isConstraintsRequestSuccess,
    isRequestFailure: isConstraintsRequestFailure,
    constraints,
    retrieveElementConstraints,
  } = useRetrieveElementConstraints(taskId);
  const {
    error,
    isRequestPending: isEditRequestPending,
    editTask,
  } = useEditTask(task);
  const { pushView } = useModal();
  const { template: planzoneTemplate } = useRetrieveSecondaryCriteriaTemplate(
    planzone.uuid,
    "projectElement",
  );
  const { template: projectTemplate } = useRetrieveSecondaryCriteriaTemplate(
    task.project.uuid,
    "projectElement",
  );
  const template = [...planzoneTemplate, ...projectTemplate];

  React.useEffect(() => {
    retrieveTask(taskId);
  }, [taskId]);

  React.useEffect(() => {
    !isConstraintsRequestPending &&
      !isConstraintsRequestSuccess &&
      !isConstraintsRequestFailure &&
      retrieveElementConstraints({ elementId: taskId, elementType: "task" });
  }, [taskId]);

  return (
    <WorkspaceContext type="project" project={task.project}>
      <Safeguard
        useCases={[
          UseCases.editTask,
          UseCases.postComment,
          UseCases.allocateResources,
        ]}
        context={{ task }}
      >
        {({ canEditTask, canPostComment, canAllocateResources }) => (
          <div
            className={classNames(
              { [styles.task]: !isRequestPending },
              { [styles.taskLoading]: isRequestPending },
            )}
          >
            {isRequestPending ? (
              <div className={styles.loader}>
                <div className={styles.loaderMain}>
                  <Loader />
                </div>
              </div>
            ) : (
              <Modal
                headerClassName={styles.header}
                closeIconClassName={styles.icon}
                header={
                  <div className={styles.contentHeader}>
                    <h1 className={styles.title}>
                      <Breadcrumbs
                        onNavigate={close}
                        breadcrumbs={[
                          {
                            name: task.project.name,
                            to: `/projects/${task.project.uuid}`,
                            icon: <FolderIcon className={styles.folderIcon} />,
                          },
                          ...(task.project.rootList.uuid !== task.list.uuid
                            ? [
                                {
                                  name: task.list.name,
                                  click: () => {
                                    close();
                                    pushView({
                                      key: UseCases.retrieveList,
                                      data: { listId: task.list.uuid },
                                    });
                                  },
                                },
                              ]
                            : []),
                          { name: task.name },
                        ]}
                      />
                    </h1>

                    <div
                      className={classNames(styles.rightContent, {
                        [styles.rightContentMultiProject]: isMultiProject,
                      })}
                    >
                      {isMultiProject && (
                        <ProjectRedirection
                          className={styles.contentProjectRedirection}
                          projectId={task.project.uuid}
                          close={close}
                        />
                      )}
                      <div>
                        <Timestamp
                          task={task}
                          isMultiProject={isMultiProject}
                        />
                      </div>
                    </div>
                  </div>
                }
                close={close}
              >
                {isRequestFailure ? (
                  <div className={styles.scenePlaceholder}>
                    <ScenePlaceholder goBack={close} />
                  </div>
                ) : (
                  <div className={styles.contentModal}>
                    <div className={styles.main}>
                      <div className={styles.content}>
                        <div className={styles.scrollWrapper}>
                          {constraints.length > 0 && (
                            <div className={styles.constraints}>
                              <ConstraintsPopup
                                elementId={taskId}
                                elementType="task"
                                direction="right"
                                constraintsNbr={constraints.length}
                                project={task.project}
                              />
                              <p className={styles.constraintsText}>
                                {t("taskRetrieval.constraint")}
                              </p>
                            </div>
                          )}
                          <div className={styles.details}>
                            <Details
                              task={task}
                              error={error}
                              readOnly={!canEditTask}
                            />
                          </div>
                          <div className={styles.list}>
                            <Allocations
                              task={task}
                              project={task.project}
                              allocations={task.allocations}
                              readOnly={!canAllocateResources}
                            />
                          </div>
                          <Safeguard
                            useCases={[
                              UseCases.editProjectElementDetailsConfiguration,
                            ]}
                            context={{ task }}
                          >
                            {({ canEditProjectElementDetailsConfiguration }) =>
                              (template.length > 0 ||
                                canEditProjectElementDetailsConfiguration) && (
                                <div className={styles.secondaryCriteria}>
                                  <div className={styles.row}>
                                    <p className={styles.label}>
                                      {t("taskRetrieval.secondaryCriteria")}
                                    </p>

                                    {canEditProjectElementDetailsConfiguration ? (
                                      <div
                                        className={styles.configurationIconBox}
                                        onClick={() =>
                                          pushView({
                                            key: UseCases.retrieveProjectElementDetailsConfiguration,
                                            data: { project: task.project },
                                          })
                                        }
                                      >
                                        <CogWheelIcon
                                          className={styles.configurationIcon}
                                        />
                                      </div>
                                    ) : (
                                      <div
                                        className={styles.contextualMenuIcon}
                                      />
                                    )}
                                  </div>
                                  <ProjectElementsSecondaryCriteria
                                    project={task.project}
                                    criteriaForms={template.reduce(
                                      (acc, criteria) => ({
                                        ...acc,
                                        [criteria.uuid]: (
                                          <SecondaryCriteriaForm
                                            criteria={criteria}
                                            value={
                                              task.attributes
                                                ? task.attributes[
                                                    criteria.uuid
                                                  ] || ""
                                                : ""
                                            }
                                            flexible
                                            disabled={isEditRequestPending}
                                            submit={(value) =>
                                              editTask({
                                                task,
                                                nextTask: {
                                                  ...task,
                                                  attributes: {
                                                    ...task.attributes,
                                                    [criteria.uuid]: value,
                                                  },
                                                },
                                              })
                                            }
                                          />
                                        ),
                                      }),
                                      {},
                                    )}
                                  />
                                </div>
                              )
                            }
                          </Safeguard>
                          <ElementAttachments
                            project={task.project}
                            element={task}
                            elementType="TASK"
                            readOnly={!canEditTask}
                          />
                        </div>
                      </div>
                      <div className={styles.timeline}>
                        <div className={styles.timestamp}>
                          <DatesPickers
                            task={task}
                            project={task.project}
                            error={error}
                            readOnly={!canEditTask}
                          />
                        </div>
                        <div className={styles.comments}>
                          {!task.project.isTemplate && (
                            <Comments
                              subject={task.uuid}
                              type="task"
                              canPost={canPostComment}
                              project={task.project.uuid}
                            />
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </Modal>
            )}
          </div>
        )}
      </Safeguard>
    </WorkspaceContext>
  );
}

export { Task };
